import { useState } from 'react';
import { getConnectorConfig } from '../utils/connectorConfig';
import {
  CheckCircle2,
  Loader2,
  FileText,
  Search,
  MessageSquare,
  Download,
  Copy,
  Check,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  CheckCircle,
  ShieldCheck
} from 'lucide-react';
import apiClient from '../services/api';
import useStore from '../store/useStore';

const ConsumerDataExchange = () => {
  const { addNotification } = useStore();

  // Workflow state
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [workflowData, setWorkflowData] = useState({
    // Provider URLs
    providerManagementUrl: '',
    providerDspUrl: '',

    // Consumer URLs
    consumerManagementUrl: '',
    consumerDspUrl: '',

    // API Key for EDC
    apiKey: getConnectorConfig('consumerApiKey'),

    // Catalog
    catalogData: null,
    availableOffers: [],
    selectedOffer: null,

    // Negotiation
    negotiationId: '',
    contractAgreementId: '',
    negotiationState: '',

    // Transfer
    transferId: '',
    destination: {
      '@type': 'DataAddress',
      type: 'HttpData',
      baseUrl: 'https://enact-dataspace.iti.gr/api/v1/data-exchange/data-receiver',
      method: 'POST',
      contentType: 'application/json'
    }
  });

  const [loading, setLoading] = useState(false);
  const [stepResults, setStepResults] = useState({});
  const [error, setError] = useState(null);

  // Extract human-readable constraint info from an ODRL policy
  const getPolicyConstraints = (policy) => {
    if (!policy) return [];
    const constraints = [];
    const permissions = Array.isArray(policy['odrl:permission'])
      ? policy['odrl:permission']
      : policy['odrl:permission'] ? [policy['odrl:permission']] : [];

    for (const perm of permissions) {
      let rawConstraints = perm['odrl:constraint'];
      if (!rawConstraints) continue;

      // Handle LogicalConstraint (AND)
      if (rawConstraints['odrl:and']) {
        rawConstraints = rawConstraints['odrl:and'];
      }
      if (!Array.isArray(rawConstraints)) {
        rawConstraints = [rawConstraints];
      }

      for (const c of rawConstraints) {
        const left = c['odrl:leftOperand'];
        const op = c['odrl:operator']?.['@id'] || c['odrl:operator'];
        const right = c['odrl:rightOperand'];
        const value = typeof right === 'object' ? right['@value'] : right;

        if (left === 'edc:participantId') {
          constraints.push({ type: 'participantId', label: 'Participant ID', value });
        } else if (left === 'edc:dateTime' && (op === 'odrl:lteq' || op?.includes('lteq'))) {
          constraints.push({ type: 'time', label: 'Valid until', value });
        } else if (left === 'edc:dateTime' && (op === 'odrl:gteq' || op?.includes('gteq'))) {
          constraints.push({ type: 'time', label: 'Valid from', value });
        } else if (left === 'PURPOSE') {
          constraints.push({ type: 'purpose', label: 'Purpose', value });
        }
      }
    }
    return constraints;
  };

  const steps = [
    { id: 'urls', title: 'Configuration', description: 'Set Consumer & Provider URLs', icon: FileText },
    { id: 'catalog', title: 'Browse Catalog', description: 'View available assets', icon: Search },
    { id: 'negotiate', title: 'Negotiate Contract', description: 'Start negotiation', icon: MessageSquare },
    { id: 'transfer', title: 'Transfer Data', description: 'Initiate data transfer', icon: Download }
  ];

  const updateWorkflowData = (key, value) => {
    setWorkflowData(prev => ({ ...prev, [key]: value }));
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getErrorMessage = (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.response?.data?.details) {
      return error.response.data.details.join(', ');
    }
    if (error.response?.data) {
      return JSON.stringify(error.response.data);
    }
    return error.message || 'Unknown error occurred';
  };

  // Step 0: URL Configuration
  const renderUrlConfiguration = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold mb-4">Consumer & Provider EDC Configuration</h3>

      {/* API Key */}
      <div className="bg-yellow-900/10 border border-yellow-800 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-400 mb-3 flex items-center gap-2">
          <ShieldCheck size={18} />
          EDC API Key
        </h4>
        <div>
          <label className="label">API Key</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1 font-mono"
              value={workflowData.apiKey}
              onChange={(e) => updateWorkflowData('apiKey', e.target.value)}
            />
            <button
              onClick={() => copyToClipboard(workflowData.apiKey, 'apiKey')}
              className="btn btn-secondary px-3"
            >
              {copiedField === 'apiKey' ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Required for EDC Management API authentication</p>
        </div>
      </div>

      {/* Provider Section */}
      <div className="bg-blue-900/10 border border-blue-800 rounded-lg p-4">
        <h4 className="font-semibold text-blue-400 mb-4 flex items-center gap-2">
          <FileText size={18} />
          Provider Connector
        </h4>

        <div className="space-y-4">
          <div>
            <label className="label">Management API URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.providerManagementUrl}
                onChange={(e) => updateWorkflowData('providerManagementUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.providerManagementUrl, 'providerMgmt')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'providerMgmt' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">DSP Endpoint URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.providerDspUrl}
                onChange={(e) => updateWorkflowData('providerDspUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.providerDspUrl, 'providerDsp')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'providerDsp' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Consumer Section */}
      <div className="bg-green-900/10 border border-green-800 rounded-lg p-4">
        <h4 className="font-semibold text-green-400 mb-4 flex items-center gap-2">
          <FileText size={18} />
          Consumer Connector
        </h4>

        <div className="space-y-4">
          <div>
            <label className="label">Management API URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.consumerManagementUrl}
                onChange={(e) => updateWorkflowData('consumerManagementUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.consumerManagementUrl, 'consumerMgmt')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'consumerMgmt' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">DSP Endpoint URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                value={workflowData.consumerDspUrl}
                onChange={(e) => updateWorkflowData('consumerDspUrl', e.target.value)}
              />
              <button
                onClick={() => copyToClipboard(workflowData.consumerDspUrl, 'consumerDsp')}
                className="btn btn-secondary px-3"
              >
                {copiedField === 'consumerDsp' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 1: Browse Catalog
  const handleBrowseCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/data-exchange/consumer/catalog', {
        params: {
          providerUrl: workflowData.providerManagementUrl,
          consumerUrl: workflowData.consumerManagementUrl,
          providerDspUrl: workflowData.providerDspUrl,
          consumerDspUrl: workflowData.consumerDspUrl,
          apiKey: workflowData.apiKey
        }
      });

      setStepResults(prev => ({ ...prev, catalog: response.data }));
      updateWorkflowData('catalogData', response.data.catalog);
      updateWorkflowData('availableOffers', response.data.availableOffers || []);

      addNotification({
        type: 'success',
        message: `Found ${response.data.availableOffers?.length || 0} available offers!`
      });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to browse catalog: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderCatalogBrowser = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Browse Provider Catalog</h3>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleBrowseCatalog}
        disabled={loading}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
        Fetch Catalog
      </button>

      {workflowData.availableOffers.length > 0 && (
        <div className="space-y-4">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">Available Assets ({workflowData.availableOffers.length})</h4>
              <span className="text-xs text-gray-400">
                Provider: {stepResults.catalog?.summary?.providerParticipant}
              </span>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {workflowData.availableOffers.map((offer, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    workflowData.selectedOffer?.assetId === offer.assetId
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-gray-700 hover:border-blue-500/50'
                  }`}
                  onClick={() => {
                    updateWorkflowData('selectedOffer', offer);
                    addNotification({ type: 'info', message: `Selected: ${offer.title}` });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-200">{offer.title}</p>
                      <p className="text-sm text-gray-400 mt-1">{offer.description}</p>
                      <p className="text-xs text-gray-500 font-mono mt-2">
                        Asset: {offer.assetId}
                      </p>
                      {/* Policy constraints display */}
                      {(() => {
                        const constraints = getPolicyConstraints(offer.policy);
                        if (constraints.length === 0) return null;
                        return (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {constraints.map((c, ci) => (
                              <span
                                key={ci}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  c.type === 'participantId'
                                    ? 'bg-purple-900/30 text-purple-300 border border-purple-700/50'
                                    : c.type === 'purpose'
                                    ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50'
                                    : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50'
                                }`}
                                title={`${c.label}: ${c.value}`}
                              >
                                <ShieldCheck size={10} />
                                {c.label}: {c.value?.length > 24 ? c.value.slice(0, 24) + '...' : c.value}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    {workflowData.selectedOffer?.assetId === offer.assetId && (
                      <CheckCircle2 className="text-blue-400 flex-shrink-0 ml-4" size={24} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => setCurrentStep(2)}
            disabled={!workflowData.selectedOffer}
            className="btn btn-primary w-full"
          >
            Continue with Selected Asset
            <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );

  // Step 2: Negotiate Contract
  const handleNegotiateContract = async () => {
    setLoading(true);
    setError(null);
    try {
      const offer = workflowData.selectedOffer;

      const response = await apiClient.post('/data-exchange/consumer/negotiate', {
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        assetId: offer.assetId,
        offerId: offer.offerId,
        policy: offer.policy,
        providerId: stepResults.catalog?.summary?.providerParticipant || workflowData.providerParticipantId || 'connector-provider',
        apiKey: workflowData.apiKey
      });

      const negotiationId = response.data.negotiationId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, negotiation: response.data }));
      updateWorkflowData('negotiationId', negotiationId);

      addNotification({ type: 'success', message: 'Contract negotiation started!' });
      setTimeout(() => checkNegotiationStatus(negotiationId), 3000);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to negotiate: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const checkNegotiationStatus = async (negotiationId) => {
    try {
      const response = await apiClient.get(`/data-exchange/consumer/negotiation/${negotiationId}`, {
        params: {
          providerUrl: workflowData.providerManagementUrl,
          consumerUrl: workflowData.consumerManagementUrl,
          providerDspUrl: workflowData.providerDspUrl,
          consumerDspUrl: workflowData.consumerDspUrl,
          apiKey: workflowData.apiKey
        }
      });

      setStepResults(prev => ({ ...prev, negotiationStatus: response.data }));
      updateWorkflowData('negotiationState', response.data.state || response.data.status);

      if (response.data.contractAgreementId) {
        updateWorkflowData('contractAgreementId', response.data.contractAgreementId);
        addNotification({ type: 'success', message: 'Contract agreement reached!' });
      }
    } catch (error) {
      console.error('Failed to check negotiation status:', error);
    }
  };

  const renderContractNegotiation = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Negotiate Contract</h3>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {workflowData.selectedOffer && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <h4 className="font-medium mb-2">Selected Asset</h4>
          <p className="text-sm text-gray-300">{workflowData.selectedOffer.title}</p>
          <p className="text-xs text-gray-500 font-mono mt-1">{workflowData.selectedOffer.assetId}</p>
        </div>
      )}

      <button
        onClick={handleNegotiateContract}
        disabled={loading || !workflowData.selectedOffer}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <MessageSquare size={16} />}
        Start Negotiation
      </button>

      {workflowData.negotiationId && (
        <div className="space-y-3">
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
            {!workflowData.contractAgreementId ? (
              <p className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                <Loader2 className="animate-spin text-blue-400" size={20} />
                Negotiation In Progress
              </p>
            ) : (
              <p className="text-green-400 font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                Negotiation Complete
              </p>
            )}
            <p className="text-sm text-gray-400">
              Negotiation ID: <span className="font-mono text-xs">{workflowData.negotiationId}</span>
            </p>
            {workflowData.contractAgreementId && (
              <p className="text-green-400 mt-2">
                ✓ Agreement ID: <span className="font-mono text-xs break-all">{workflowData.contractAgreementId}</span>
              </p>
            )}
          </div>

          <button
            onClick={() => checkNegotiationStatus(workflowData.negotiationId)}
            className="btn btn-secondary w-full"
            disabled={!!workflowData.contractAgreementId}
          >
            <RefreshCw size={16} />
            Check Status
          </button>

          {workflowData.contractAgreementId && (
            <button
              onClick={() => setCurrentStep(3)}
              className="btn btn-primary w-full"
            >
              Continue to Transfer
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );

  // Step 3: Transfer Data
  const handleTransferData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/data-exchange/consumer/transfer', {
        providerUrl: workflowData.providerManagementUrl,
        consumerUrl: workflowData.consumerManagementUrl,
        providerDspUrl: workflowData.providerDspUrl,
        consumerDspUrl: workflowData.consumerDspUrl,
        contractId: workflowData.contractAgreementId,
        assetId: workflowData.selectedOffer?.assetId,
        destination: workflowData.destination,
        apiKey: workflowData.apiKey
      });

      const transferId = response.data.transferId || response.data.response?.['@id'];
      setStepResults(prev => ({ ...prev, transfer: response.data }));
      updateWorkflowData('transferId', transferId);

      addNotification({ type: 'success', message: 'Data transfer initiated successfully!' });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      addNotification({ type: 'error', message: `Failed to transfer: ${errorMsg}` });
    } finally {
      setLoading(false);
    }
  };

  const renderDataTransfer = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Initiate Data Transfer</h3>

      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-sm text-gray-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <h4 className="font-medium mb-3">Transfer Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Contract Agreement:</span>
            <span className="text-blue-400 text-xs font-mono break-all ml-2">
              {workflowData.contractAgreementId || 'Pending'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Asset ID:</span>
            <span className="text-blue-400 text-xs font-mono">
              {workflowData.selectedOffer?.assetId}
            </span>
          </div>
        </div>
      </div>

      <div>
        <label className="label">Destination URL</label>
        <input
          type="text"
          className="input"
          value={workflowData.destination.baseUrl}
          onChange={(e) => updateWorkflowData('destination', {
            ...workflowData.destination,
            baseUrl: e.target.value
          })}
        />
      </div>

      <button
        onClick={handleTransferData}
        disabled={loading || !workflowData.contractAgreementId}
        className="btn btn-primary w-full"
      >
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
        Start Transfer
      </button>

      {workflowData.transferId && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
          <p className="text-green-400 font-medium mb-2">✓ Transfer Initiated Successfully</p>
          <p className="text-sm text-gray-400">
            Transfer ID: <span className="font-mono text-xs">{workflowData.transferId}</span>
          </p>
          <button
            onClick={() => {
              setCurrentStep(0);
              setStepResults({});
              setWorkflowData({
                ...workflowData,
                catalogData: null,
                availableOffers: [],
                selectedOffer: null,
                negotiationId: '',
                contractAgreementId: '',
                negotiationState: '',
                transferId: ''
              });
              addNotification({ type: 'success', message: 'Ready to browse catalog again!' });
            }}
            className="btn btn-secondary w-full mt-4"
          >
            Start New Transfer
          </button>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderUrlConfiguration();
      case 1: return renderCatalogBrowser();
      case 2: return renderContractNegotiation();
      case 3: return renderDataTransfer();
      default: return renderUrlConfiguration();
    }
  };

  return (
    <div className="p-6 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Consumer: Browse & Transfer Data</h1>
        <p className="text-gray-400">Discover and access data assets from the dataspace catalog</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center justify-between mb-4 min-w-max">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep || (idx === 3 && workflowData.transferId);

            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => setCurrentStep(idx)}
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all
                      ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50' : ''}
                      ${isCompleted ? 'bg-green-600 text-white' : ''}
                      ${!isActive && !isCompleted ? 'step-inactive' : ''}
                    `}
                  >
                    {isCompleted ? <CheckCircle2 size={20} /> : <Icon size={20} />}
                  </button>
                  <div className="text-center">
                    <p className={`text-xs font-medium ${isActive ? 'text-blue-400' : 'text-gray-400'}`}>
                      {step.title}
                    </p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 ${isCompleted ? 'bg-green-600' : 'step-connector-inactive'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="card max-w-4xl mx-auto">
        {renderStepContent()}

        {/* Navigation */}
        {!workflowData.transferId && (
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-800">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="btn btn-secondary"
            >
              Previous
            </button>

            {currentStep < steps.length - 1 && currentStep !== 1 && currentStep !== 2 && (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="btn btn-primary"
              >
                Next
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerDataExchange;
